import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { api, errMsg, openPdf } from "@/lib/api";
import { useT, Bi } from "@/lib/i18n";
import { inr, monthLabel, todayISO } from "@/lib/format";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, MoneyInput, Chips } from "@/components/form";

export type RentRecord = { id: string; month: string; amount_due: number; outstanding: number; status: string; tenant_name?: string; is_prorated?: boolean; days_billed?: number; days_in_month?: number };

export function downloadReceipt(p: { id: string; receipt_no: string }) {
  openPdf(`/payments/${p.id}/receipt.pdf`, `${p.receipt_no}.pdf`).catch((e) => toast.error(errMsg(e)));
}

export function MarkPaidSheet({ record, onClose, onSaved }: { record: RentRecord | null; onClose: () => void; onSaved: () => void }) {
  const { t, lang } = useT();
  const [amount, setAmount] = useState(0);
  const [mode, setMode] = useState<"cash" | "upi" | "bank">("upi");
  const [paidOn, setPaidOn] = useState(todayISO());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (record) { setAmount(record.outstanding); setNote(""); setPaidOn(todayISO()); }
  }, [record]);
  if (!record) return null;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/rent/${record.id}/payments`, { amount, mode, paid_on: paidOn, note });
      toast.success(`${t("payment_saved")} · ${r.data.receipt_no}`, {
        action: { label: t("receipt"), onClick: () => downloadReceipt(r.data) }, duration: 8000,
      });
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet open={!!record} onOpenChange={(o) => !o && onClose()} testId="mark-paid-sheet"
      title={<Bi k="mark_paid" />} description={`${record.tenant_name || ""} · ${monthLabel(record.month, lang, true)}`}>
      <div className="space-y-4">
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 flex justify-between text-sm">
          <span>{t("balance")}</span>
          <b data-testid="mark-paid-outstanding" className="font-mono-num">{inr(record.outstanding)}</b>
        </div>
        <Field label={t("amount")}>
          <MoneyInput data-testid="mark-paid-amount-input" value={amount} onChange={setAmount} />
          <div className="flex gap-2 mt-2">
            <button data-testid="mark-paid-full-btn" onClick={() => setAmount(record.outstanding)} className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold pg-tap">{t("full_amount")}</button>
            <button data-testid="mark-paid-half-btn" onClick={() => setAmount(Math.round(record.outstanding / 2))} className="h-10 px-4 rounded-full border border-slate-200 bg-white text-sm font-semibold pg-tap">{t("half_amount")}</button>
          </div>
        </Field>
        <Field label={t("mode")}>
          <Chips testId="mark-paid-mode" value={mode} onChange={setMode}
            options={[{ value: "upi", label: t("upi") }, { value: "cash", label: t("cash") }, { value: "bank", label: t("bank") }]} />
        </Field>
        <Field label={t("paid_on")}><BigInput data-testid="mark-paid-date-input" type="date" max={todayISO()} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} /></Field>
        <Field label={`${t("note")} (${t("optional")})`}><BigInput data-testid="mark-paid-note-input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} /></Field>
        <Button data-testid="mark-paid-submit-btn" disabled={busy || amount <= 0 || amount > record.outstanding} onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("mark_paid")} · {inr(amount)}</>}
        </Button>
        <p className="text-[11px] text-slate-500 flex items-center gap-1"><Download className="h-3 w-3" />{t("receipt_footer")}</p>
      </div>
    </BottomSheet>
  );
}
